import Loading from '@/components/loading/Loading';
import { withAuthenticationRequired } from '@auth0/auth0-react';
import {
  Box,
  Button,
  Chip,
  Container,
  IconButton,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { CompassCoreModal } from './CompassCoreModal';
import { useEffect, useState } from 'react';
import MacInput from './MACFormatterField';
import {
  createDevicePlanChangeRequest,
  deleteDevicePlanChangeRequest,
  getAllDevicePlanChangeRequests,
} from '@/repository/DevicePlanChangeDAO';
import {
  DevicePlanChangeAction,
  DevicePlanChangeOnAzure,
} from '../../../../generated-client/models';
import { useUserContext } from '@/context_providers/user_context/UserProvider';
import { NavigatorDataGrid } from '@/components/data/NavigatorDataGrid';
import {
  ErrorStringKeys,
  StringKeys,
  SuccessStringKeys,
} from '@/resources/strings/StringKeys';
import { t } from 'i18next';
import {
  ToastType,
  useToast,
} from '@/context_providers/toast_context/ToastContext';
import { ResponseError } from '../../../../generated-client/runtime';
import { Cancel } from '@mui/icons-material';
import COLORS from '@/resources/style/Themes';

const CompassCoreManagement = () => {
  const { bearerToken } = useUserContext();
  const { showToast } = useToast();
  const theme = useTheme();

  const [devicePlanRequests, setDevicePlanRequests] = useState<
    DevicePlanChangeOnAzure[]
  >([]);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [canceledDevicePlanChangeId, setCanceledDevicePlanChangeId] =
    useState('');

  const fetchRequests = async () => {
    try {
      const requests = await getAllDevicePlanChangeRequests();
      setDevicePlanRequests(requests);
    } catch (error) {
      console.error('Error fetching device plan change requests:', error);
    }
  };

  useEffect(() => {
    // I want to reuse the fetchRequests function later,
    // so I'm defining it separately and then calling it here
    // The fetchData function is just a wrapper to call fetchRequests, and can be expanded later if needed
    // (e.g. for additional data fetching or processing)
    const fetchData = async () => {
      await fetchRequests();
    };
    if (bearerToken) {
      fetchData();
    }
  }, [bearerToken]);

  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);
  const [upgradeModalMACAddress, setUpgradeModalMACAddress] = useState('');
  const [upgradeMacError, setUpgradeMacError] = useState('');

  const handleOpenUpgradeModal = () => {
    setIsUpgradeModalOpen(true);
    setUpgradeMacError('');
  };

  const handleCloseUpgradeModal = () => {
    setIsUpgradeModalOpen(false);
    setUpgradeModalMACAddress('');
    setUpgradeMacError('');
  };

  const [isDowngradeModalOpen, setIsDowngradeModalOpen] = useState(false);
  const [downgradeModalMACAddress, setDowngradeModalMACAddress] = useState('');
  const [downgradeMacError, setDowngradeMacError] = useState('');

  const handleOpenDowngradeModal = () => {
    setIsDowngradeModalOpen(true);
    setDowngradeMacError('');
  };

  const handleCloseDowngradeModal = () => {
    setIsDowngradeModalOpen(false);
    setDowngradeModalMACAddress('');
    setDowngradeMacError('');
  };

  const cancelDevicePlanChangeRequest = async (id: string) => {
    try {
      await deleteDevicePlanChangeRequest(id);
      fetchRequests(); // Refresh the data grid to remove the cancelled request
      showToast(
        t(SuccessStringKeys.REQUEST_CANCELLED_SUCCESSFULLY),
        ToastType.SUCCESS,
      );
    } catch (error) {
      console.error('Error cancelling device plan change request:', error);
      showToast(t(ErrorStringKeys.FAILED_TO_CANCEL_REQUEST), ToastType.DANGER);
    }
  };

  const columns: GridColDef<DevicePlanChangeOnAzure>[] = [
    {
      field: 'mac_address',
      headerName: t(StringKeys.MAC_ADDRESS),
      headerClassName: 'first',
      cellClassName: 'first',
      valueFormatter: (params) => {
        // format the mac address to be all uppercase and have colons every 2 characters
        try {
          const mac = params as string;
          const formattedMac = mac
            .toUpperCase()
            .match(/.{1,2}/g)
            ?.join(':');
          return formattedMac || mac;
        } catch (error) {
          console.error('Error formatting MAC address:', error);
          return params;
        }
      },
      flex: 1,
    },
    {
      field: 'created_at',
      headerName: t(StringKeys.REQUESTED_AT),
      flex: 1,
      valueFormatter: (params) => {
        try {
          const date = new Date(params * 1000); // Convert seconds to milliseconds
          return date.toLocaleString();
        } catch (error) {
          console.error('Error formatting created_at date:', error);
          return params;
        }
      },
    },
    {
      field: 'completed_at',
      headerName: t(StringKeys.STATUS),
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Chip
            label={
              params.value !== undefined
                ? t(StringKeys.COMPLETED)
                : t(StringKeys.PENDING)
            }
            variant="outlined"
            color={params.value !== undefined ? 'complete' : 'pending'}
          />
        </Box>
      ),
      flex: 1,
    },
    {
      field: 'action',
      headerName: t(StringKeys.ACTION),
      flex: 1,
      valueFormatter: (params) => {
        // capitalize the first letter
        try {
          const action = params as DevicePlanChangeAction;
          return action.charAt(0).toUpperCase() + action.slice(1);
        } catch (error) {
          console.error('Error formatting action value:', error);
          return t(StringKeys.ERROR);
        }
      },
    },
    {
      field: 'cancel',
      headerName: t(StringKeys.CANCEL),
      renderCell: (params) => {
        const isCompleted = params.row.completed_at !== undefined;
        return (
          !isCompleted && (
            <IconButton
              aria-label="cancel"
              onClick={() => {
                setCanceledDevicePlanChangeId(params.row.id);
                setIsCancelModalOpen(true);
              }}
              color="error"
            >
              <Cancel />
            </IconButton>
          )
        );
      },
    },
  ];

  const upgradeAction = async () => {
    try {
      setUpgradeMacError('');
      if (upgradeModalMACAddress.length < 17) {
        setUpgradeMacError(t(ErrorStringKeys.INVALID_MAC_ADDRESS));
        return;
      }
      await createDevicePlanChangeRequest(
        upgradeModalMACAddress,
        DevicePlanChangeAction.Upgrade,
      );
      fetchRequests(); // Refresh the data grid to show the new request
      handleCloseUpgradeModal(); // Close the modal after submitting
      setUpgradeModalMACAddress(''); // Clear the MAC address input after submitting
      showToast(
        t(SuccessStringKeys.UPGRADE_REQUEST_CREATED_SUCCESSFULLY),
        ToastType.SUCCESS,
      );
    } catch (error) {
      if (error instanceof ResponseError && error.response?.status === 409) {
        setUpgradeMacError(t(StringKeys.MAC_ADDRESS_REQUEST_ALREADY_EXISTS));
        return;
      }

      showToast(
        t(ErrorStringKeys.FAILED_TO_CREATE_UPGRADE_REQUEST),
        ToastType.DANGER,
      );
    }
  };

  const downgradeAction = async () => {
    try {
      setDowngradeMacError('');
      if (downgradeModalMACAddress.length < 17) {
        setDowngradeMacError(t(ErrorStringKeys.INVALID_MAC_ADDRESS));
        return;
      }
      await createDevicePlanChangeRequest(
        downgradeModalMACAddress,
        DevicePlanChangeAction.Downgrade,
      );
      fetchRequests(); // Refresh the data grid to show the new request
      handleCloseDowngradeModal(); // Close the modal after submitting
      setDowngradeModalMACAddress(''); // Clear the MAC address input after submitting
      showToast(
        t(SuccessStringKeys.DOWNGRADE_REQUEST_CREATED_SUCCESSFULLY),
        ToastType.SUCCESS,
      );
    } catch (error) {
      if (error instanceof ResponseError && error.response?.status === 409) {
        setDowngradeMacError(t(StringKeys.MAC_ADDRESS_REQUEST_ALREADY_EXISTS));
        return;
      }

      showToast(
        t(ErrorStringKeys.FAILED_TO_CREATE_DOWNGRADE_REQUEST),
        ToastType.DANGER,
        true,
      );
    }
  };

  return (
    <Container maxWidth="lg">
      <Stack
        spacing={2}
        mt={4}
        mb={2}
        direction={'row'}
        justifyContent={'space-between'}
      >
        <Typography variant="h4" fontWeight={'bold'}>
          Compass Core Transactions
        </Typography>
        <Stack
          spacing={2}
          direction={'row'}
          alignItems={'end'}
          justifyContent={'end'}
        >
          <Button onClick={handleOpenDowngradeModal} variant="outlined">
            {t(StringKeys.NEW_DOWNGRADE)}
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={handleOpenUpgradeModal}
          >
            {t(StringKeys.NEW_UPGRADE)}
          </Button>
        </Stack>
      </Stack>
      <Box
        sx={{
          padding: '1rem',
          marginBottom: '1rem',
          borderRadius: '15px',
          border: '2px solid',
          borderColor: theme.palette.error.main,
        }}
      >
        <Typography variant="body1" mb={2}>
          {t(StringKeys.COMPASS_CORE_MANAGEMENT_DESCRIPTION)}
        </Typography>
      </Box>
      <NavigatorDataGrid
        sx={{
          '& .MuiDataGrid-menuIcon .MuiIconButton-root': {
            color: `${COLORS.GREY} !important`,
          },
          '& .MuiDataGrid-sortIcon': {
            color: `${COLORS.GREY} !important`,
          },
          '& .MuiDataGrid-columnSeparator': {
            color: `${COLORS.GREY} !important`,
          },
        }}
        rows={devicePlanRequests}
        columns={columns}
      />
      <CompassCoreModal
        open={isUpgradeModalOpen}
        title={t(StringKeys.UPGRADE_TO_PRO)}
        onClose={handleCloseUpgradeModal}
        size="xl"
        submitButtonText={t(StringKeys.UPGRADE)}
        onSubmit={async () => {
          await upgradeAction();
        }}
      >
        <Container
          sx={{
            minWidth: '25rem',
            marginY: '1rem',
          }}
        >
          <MacInput
            mac={upgradeModalMACAddress}
            setMac={setUpgradeModalMACAddress}
            errorText={upgradeMacError}
            onMacChange={() => setUpgradeMacError('')}
          />
        </Container>
      </CompassCoreModal>
      <CompassCoreModal
        open={isDowngradeModalOpen}
        title={t(StringKeys.DOWNGRADE_TO_CORE)}
        onClose={handleCloseDowngradeModal}
        size="xl"
        submitButtonText={t(StringKeys.DOWNGRADE)}
        onSubmit={async () => {
          await downgradeAction();
        }}
      >
        <Container
          sx={{
            minWidth: '25rem',
            marginY: '1rem',
          }}
        >
          <MacInput
            mac={downgradeModalMACAddress}
            setMac={setDowngradeModalMACAddress}
            errorText={downgradeMacError}
            onMacChange={() => setDowngradeMacError('')}
          />
        </Container>
      </CompassCoreModal>
      <CompassCoreModal
        open={isCancelModalOpen}
        onSubmit={() => {
          cancelDevicePlanChangeRequest(canceledDevicePlanChangeId);
          setIsCancelModalOpen(false);
        }}
        title={t(SuccessStringKeys.CANCEL_REQUEST)}
        onClose={() => setIsCancelModalOpen(false)}
        size="sm"
        submitButtonText={t(StringKeys.YES)}
        submitButtonColor="error"
        secondaryButtonText={t(StringKeys.NO)}
      >
        <Typography>
          {t(SuccessStringKeys.CANCEL_REQUEST_CONFIRMATION)}
        </Typography>
      </CompassCoreModal>
    </Container>
  );
};

const CompassCoreManagementWithAuth = withAuthenticationRequired(
  CompassCoreManagement,
  {
    onRedirecting: () => <Loading loadingText="Loading..." />,
  },
);
export default CompassCoreManagementWithAuth;
